<div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-xl">
  <div>
    <h1 class="font-headline-lg text-headline-lg text-text-main dark:text-on-surface mb-1">Scheduling</h1>
    @if (error()) {
      <p class="text-error font-body-sm text-body-sm">{{ error() }}</p>
    }
  </div>
</div>

<div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl p-md flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-gutter shadow-sm">
  <div class="flex items-center gap-3">
    <button (click)="goToPrevious()" class="w-9 h-9 rounded-lg border border-border-subtle dark:border-outline-variant hover:bg-surface-variant dark:hover:bg-surface-variant flex items-center justify-center transition-colors">
      <span class="material-symbols-outlined text-[20px]">chevron_left</span>
    </button>
    <button (click)="goToToday()" class="h-9 px-4 rounded-lg border border-border-subtle dark:border-outline-variant hover:bg-surface-variant dark:hover:bg-surface-variant text-text-main dark:text-on-surface font-label-sm transition-colors">
      Today
    </button>
    <button (click)="goToNext()" class="w-9 h-9 rounded-lg border border-border-subtle dark:border-outline-variant hover:bg-surface-variant dark:hover:bg-surface-variant flex items-center justify-center transition-colors">
      <span class="material-symbols-outlined text-[20px]">chevron_right</span>
    </button>
    <span class="font-label-md text-text-main dark:text-on-surface ml-2">{{ rangeLabel() }}</span>
  </div>
  <div class="flex items-center gap-1 bg-surface dark:bg-surface-container-highest rounded-lg p-1">
    <button (click)="setViewMode('day')"
            [ngClass]="viewMode() === 'day' ? 'bg-white dark:bg-surface-container' : 'text-text-muted dark:text-on-surface-variant'"
            [style.color]="viewMode() === 'day' ? '#00B9F2' : null"
            class="px-4 py-1.5 rounded-md font-label-sm transition-colors">
      Day
    </button>
    <button (click)="setViewMode('week')"
            [ngClass]="viewMode() === 'week' ? 'bg-white dark:bg-surface-container' : 'text-text-muted dark:text-on-surface-variant'"
            [style.color]="viewMode() === 'week' ? '#00B9F2' : null"
            class="px-4 py-1.5 rounded-md font-label-sm transition-colors">
      Week
    </button>
    <button (click)="setViewMode('month')"
            [ngClass]="viewMode() === 'month' ? 'bg-white dark:bg-surface-container' : 'text-text-muted dark:text-on-surface-variant'"
            [style.color]="viewMode() === 'month' ? '#00B9F2' : null"
            class="px-4 py-1.5 rounded-md font-label-sm transition-colors">
      Month
    </button>
  </div>
</div>

<div class="bg-background dark:bg-surface-container-low border border-border-subtle dark:border-outline-variant rounded-xl overflow-hidden shadow-sm">
  @if (loading()) {
    <p class="p-6 text-center text-text-muted dark:text-on-surface-variant font-body-sm">Loading schedule...</p>
  } @else if (viewMode() === 'month') {
    <div class="grid grid-cols-7 border-b border-border-subtle dark:border-outline-variant">
      @for (label of weekdayLabels; track label) {
        <div class="p-2 text-center font-label-sm text-text-muted dark:text-on-surface-variant">{{ label }}</div>
      }
    </div>
    <div class="grid grid-cols-7">
      @for (date of visibleDates(); track date.getTime()) {
        <div class="border-r border-b border-border-subtle dark:border-outline-variant [&:nth-child(7n)]:border-r-0 min-h-[180px] max-h-[220px] p-2 flex flex-col gap-1.5"
             [ngClass]="!isCurrentMonth(date) ? 'bg-surface/50 dark:bg-surface-container/30' : ''">
          <span class="font-label-sm w-6 h-6 flex items-center justify-center rounded-full shrink-0"
                [ngClass]="!isCurrentMonth(date) ? 'text-text-muted dark:text-on-surface-variant opacity-50' : 'text-text-main dark:text-on-surface'"
                [style.background-color]="isToday(date) ? '#00B9F2' : null"
                [style.color]="isToday(date) ? '#ffffff' : null">
            {{ formatDayNumber(date) }}
          </span>
          <div class="flex-1 flex flex-col gap-1 overflow-y-auto">
            @for (job of jobsFor(date); track job.jobId) {
              <div class="rounded-md border-l-2 pl-1.5 pr-1 py-0.5 text-xs truncate"
                   [style.background-color]="statusColor(job.status) + '0D'"
                   [style.border-left-color]="statusColor(job.status)">
                <span class="font-label-sm" [style.color]="statusColor(job.status)">{{ formatTime(job.scheduledDate) || 'No time' }}</span>
                <span class="text-text-main dark:text-on-surface"> &middot; {{ job.dealerName }}</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  } @else {
    <div class="grid" [class.grid-cols-7]="viewMode() === 'week'" [class.grid-cols-1]="viewMode() === 'day'">
      @for (date of visibleDates(); track date.getTime()) {
        <div class="border-r border-border-subtle dark:border-outline-variant last:border-r-0 flex flex-col min-h-[420px]">
          <div class="p-3 border-b border-border-subtle dark:border-outline-variant text-center"
               [ngClass]="isToday(date) ? 'bg-blue-50 dark:bg-surface-variant' : ''">
            <p class="font-label-sm text-text-muted dark:text-on-surface-variant">{{ formatDayName(date) }}</p>
            <p class="font-headline-sm text-headline-sm"
               [ngClass]="isToday(date) ? '' : 'text-text-main dark:text-on-surface'"
               [style.color]="isToday(date) ? '#00B9F2' : null">
              {{ formatDayNumber(date) }}
            </p>
          </div>
          <div class="flex-1 p-2 flex flex-col gap-2 overflow-y-auto">
            @if (jobsFor(date).length === 0) {
              <p class="text-center text-text-muted dark:text-on-surface-variant text-xs mt-4">No jobs</p>
            }
            @for (job of jobsFor(date); track job.jobId) {
              <div class="rounded-lg border p-2 text-xs"
                   [style.background-color]="statusColor(job.status) + '0D'"
                   [style.border-color]="statusColor(job.status) + '40'">
                <div class="flex items-center justify-between gap-1 mb-1">
                  <span class="font-label-sm" [style.color]="statusColor(job.status)">{{ formatTime(job.scheduledDate) || 'No time' }}</span>
                  <span class="w-1.5 h-1.5 rounded-full shrink-0" [style.background-color]="statusColor(job.status)"></span>
                </div>
                <p class="font-label-sm text-text-main dark:text-on-surface truncate">{{ job.dealerName }}</p>
                <p class="text-text-muted dark:text-on-surface-variant truncate">{{ job.technicianName || 'Unassigned' }}</p>
                <p class="text-text-muted dark:text-on-surface-variant">{{ job.status }}</p>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>
